import React, { useState, useEffect } from "react"
import {
  Box,
  Button,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  IconButton,
  Spinner,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { CheckIcon, CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons"
import axios from "axios"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"

const StoreComments = () => {
  const { selectedSupplier } = useSupplierRetailer()
  const [comments, setComments] = useState([])
  const [stores, setStores] = useState([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState({})
  const [newStore, setNewStore] = useState("")
  const [newComment, setNewComment] = useState("")
  const toast = useToast()

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")

  useEffect(() => {
    if (selectedSupplier) {
      fetchComments()
      fetchStores()
    }
  }, [selectedSupplier])

  const fetchComments = async () => {
    setLoading(true)
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/orders/get-store-comments`, {
        supplier: selectedSupplier,
      })
      if (res.data.status === 1) {
        setComments(res.data.data)
      }
    } catch (err) {
      console.error("fetchComments error:", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchStores = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/orders/get-stores`, {
        supplier: selectedSupplier,
      })
      if (res.data.status === 1) {
        setStores(res.data.data)
      }
    } catch (err) {
      console.error("fetchStores error:", err)
    }
  }

  const handleSave = async (store_code, comment) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/orders/upsert-store-comment`, {
        supplier: selectedSupplier,
        store_code,
        comment,
        userid: localStorage.getItem("userid"),
      })
      toast({ title: "Comment saved", status: "success", duration: 2000 })
      setEditing({})
      fetchComments()
    } catch (err) {
      toast({ title: "Error saving comment", description: err.message, status: "error", duration: 3000 })
    }
  }

  const handleDelete = async (id) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/orders/delete-store-comment`, { id })
      toast({ title: "Comment deleted", status: "info", duration: 2000 })
      fetchComments()
    } catch (err) {
      toast({ title: "Error deleting comment", description: err.message, status: "error", duration: 3000 })
    }
  }

  const handleAddNew = async () => {
    if (!newStore.trim() || !newComment.trim()) {
      toast({ title: "Store code and comment are required", status: "warning", duration: 2000 })
      return
    }
    await handleSave(newStore.trim(), newComment.trim())
    setNewStore("")
    setNewComment("")
  }

  if (!selectedSupplier) {
    return <Text p={4} color="gray.500">Select a supplier to manage store comments</Text>
  }

  return (
    <Box>
      <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" p={4} mb={4}>
        <Text fontSize="lg" fontWeight="bold" mb={3}>
          Add New Store Comment
        </Text>
        <Flex gap={3} align="flex-end">
          <Box>
            <Text fontSize="sm" mb={1}>Store Code</Text>
            <Input
              size="sm"
              w="150px"
              placeholder="e.g. WC44"
              value={newStore}
              onChange={(e) => setNewStore(e.target.value)}
              list="store-list"
            />
            <datalist id="store-list">
              {stores.map((s) => (
                <option key={s.site_code} value={s.site_code}>
                  {s.site_description}
                </option>
              ))}
            </datalist>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" mb={1}>Comment</Text>
            <Input
              size="sm"
              placeholder="Enter comment for this store..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddNew() }}
            />
          </Box>
          <Button size="sm" colorScheme="blue" onClick={handleAddNew}>
            Add
          </Button>
        </Flex>
      </Box>

      <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden">
        <Text fontSize="lg" fontWeight="bold" p={4} pb={2}>
          Existing Comments ({comments.length})
        </Text>

        {loading ? (
          <Flex justify="center" py={6}><Spinner /></Flex>
        ) : comments.length === 0 ? (
          <Text p={4} color="gray.500">No store comments yet for this supplier</Text>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr bg={headerBg}>
                <Th>Store Code</Th>
                <Th>Store Name</Th>
                <Th>Comment</Th>
                <Th>Updated</Th>
                <Th w="100px">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {comments.map((c) => (
                <Tr key={c.id} _hover={{ bg: hoverBg }}>
                  <Td fontWeight="bold" fontSize="sm">{c.store_code}</Td>
                  <Td fontSize="sm">{c.site_description || "-"}</Td>
                  <Td>
                    {editing[c.id] !== undefined ? (
                      <Flex gap={1}>
                        <Input
                          size="sm"
                          value={editing[c.id]}
                          onChange={(e) => setEditing((prev) => ({ ...prev, [c.id]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSave(c.store_code, editing[c.id])
                            if (e.key === "Escape") setEditing((prev) => { const n = {...prev}; delete n[c.id]; return n })
                          }}
                          autoFocus
                        />
                        <IconButton
                          aria-label="Save"
                          icon={<CheckIcon />}
                          size="sm"
                          colorScheme="green"
                          onClick={() => handleSave(c.store_code, editing[c.id])}
                        />
                        <IconButton
                          aria-label="Cancel"
                          icon={<CloseIcon />}
                          size="sm"
                          onClick={() => setEditing((prev) => { const n = {...prev}; delete n[c.id]; return n })}
                        />
                      </Flex>
                    ) : (
                      <Text fontSize="sm">{c.comment}</Text>
                    )}
                  </Td>
                  <Td fontSize="xs" color="gray.500">
                    {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : "-"}
                  </Td>
                  <Td>
                    <Flex gap={1}>
                      <IconButton
                        aria-label="Edit"
                        icon={<EditIcon />}
                        size="xs"
                        variant="outline"
                        onClick={() => setEditing({ ...editing, [c.id]: c.comment })}
                      />
                      <IconButton
                        aria-label="Delete"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleDelete(c.id)}
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  )
}

export default StoreComments
